import { cva } from "class-variance-authority";
import type { VariantProps } from "class-variance-authority";
import { Check, Minus } from "lucide-react";
import { Checkbox as CheckboxPrimitive } from "radix-ui";
import type { ComponentProps, MouseEvent, ReactNode } from "react";
import { useRef } from "react";

import { cn } from "helpers/cn";

// A `<label>` wrapper rather than a sibling `htmlFor`: the box is a `<button>`,
// which is a labelable element, so wrapping gets antd's click-the-text-to-toggle
// behaviour without an id at every call site. `relative` because inside a form
// Radix renders a hidden `position: absolute` input as the box's sibling — with no
// containing block it resolves to the page and adds a second scrollbar (see Switch).
const rootVariants = cva("relative inline-flex items-center gap-2 text-caption", {
  variants: {
    size: {
      medium: "",
      large: "",
      xlarge: "min-h-11 text-body"
    },
    checkboxPosition: {
      left: "",
      // Block-level `flex`, not the base's `inline-flex`: antd set the same, and it
      // is what makes the row fill its container (a dropdown menu's title-content
      // span, say) so `justify-between` has width to push the box to the far edge.
      right: "flex grow flex-row-reverse justify-between"
    },
    // Fills its container with the box at the near edge, leaving the rest of the row
    // to the label — what a label that has to truncate needs.
    fullWidth: {
      true: "flex w-full"
    },
    disabled: {
      true: "cursor-not-allowed opacity-40",
      false: "cursor-pointer"
    }
  },
  defaultVariants: { checkboxPosition: "left", disabled: false }
});

// Square by default, mirroring antd's `Checkbox.borderRadiusSM: 0` override. The
// tick's colour lives on the indicator, not here: antd's reset.css is unlayered, so
// its `button { color: inherit }` outranks any `text-*` utility on this element.
const boxVariants = cva(
  "flex shrink-0 items-center justify-center border border-border bg-background outline-none transition-colors data-[state=checked]:border-brand data-[state=checked]:bg-brand data-[state=indeterminate]:border-brand data-[state=indeterminate]:bg-brand focus-visible:ring-2 focus-visible:ring-brand/20 enabled:hover:border-brand",
  {
    variants: {
      isRounded: { true: "rounded-full", false: "rounded-none" },
      size: { medium: "size-4", large: "size-6", xlarge: "size-7 rounded-md" }
    },
    defaultVariants: { size: "medium", isRounded: false }
  }
);

// The tick fills the box's content area, as antd's rotated-border checkmark did.
const checkSizes = { medium: 14, large: 20, xlarge: 24 };

// The mixed state stays a short dash — antd drew it at half the box.
const minusSizes = { medium: 10, large: 16, xlarge: 20 };

type CheckboxProps = Omit<
  ComponentProps<typeof CheckboxPrimitive.Root>,
  "asChild" | "checked" | "children" | "onCheckedChange"
> &
  VariantProps<typeof boxVariants> &
  VariantProps<typeof rootVariants> & {
    checked?: boolean;
    // The mixed state is driven by `indeterminate`, and Radix only ever reports a
    // boolean back from a click, so callers never have to narrow a CheckedState.
    indeterminate?: boolean;
    onCheckedChange?: (checked: boolean) => void;
    children?: ReactNode;
    testID?: string;
  };

const Checkbox = ({
  checkboxPosition,
  checked,
  children,
  className,
  disabled = false,
  fullWidth,
  indeterminate,
  isRounded,
  onClick,
  size,
  style,
  testID = "checkbox",
  ...props
}: CheckboxProps) => {
  // Clicking the label also forwards a click to the box, so an ancestor listening
  // for clicks — the task filters hang off exactly that — would count one
  // interaction twice. Suppress the forwarded one, keeping the label's own, which
  // is the event antd bubbled too (its Checkbox locks the same way).
  const labelClickLock = useRef<number | null>(null);

  const releaseLock = () => {
    if (labelClickLock.current !== null) {
      cancelAnimationFrame(labelClickLock.current);
      labelClickLock.current = null;
    }
  };

  const handleLabelClick = () => {
    releaseLock();
    labelClickLock.current = requestAnimationFrame(() => {
      labelClickLock.current = null;
    });
  };

  const handleBoxClick = (event: MouseEvent<HTMLButtonElement>) => {
    if (labelClickLock.current !== null) {
      event.stopPropagation();
      releaseLock();
    }
    onClick?.(event);
  };

  // `className`/`style` land on the root as they did on antd's own `<label>`;
  // everything else (id, name, value, data-*, aria-*) belongs to the control.
  return (
    <label
      className={cn(
        rootVariants({ checkboxPosition, disabled, fullWidth, size }),
        className
      )}
      data-slot="checkbox-root"
      style={style}
      onClick={handleLabelClick}
    >
      <CheckboxPrimitive.Root
        checked={indeterminate ? "indeterminate" : checked}
        className={cn(boxVariants({ isRounded, size }))}
        data-slot="checkbox"
        data-testid={testID}
        disabled={disabled}
        {...props}
        onClick={handleBoxClick}
      >
        <CheckboxPrimitive.Indicator
          className="text-white"
          data-slot="checkbox-indicator"
        >
          {indeterminate ? (
            <Minus size={minusSizes[size ?? "medium"]} strokeWidth={3} />
          ) : (
            <Check size={checkSizes[size ?? "medium"]} strokeWidth={3} />
          )}
        </CheckboxPrimitive.Indicator>
      </CheckboxPrimitive.Root>
      {children != null && (
        <span
          className={cn(
            checkboxPosition === "right" && "flex-1",
            // `min-w-0` so a truncating label can shrink below its text width.
            fullWidth && "min-w-0 flex-1"
          )}
        >
          {children}
        </span>
      )}
    </label>
  );
};

export default Checkbox;
export { boxVariants, rootVariants };
export type { CheckboxProps };
